/**
 * eDesk Widget Controller - Version 3.5
 * Wrapped in IIFE to prevent global namespace pollution
 */
(function () {

  // Get translations with safe fallbacks
  const translations = (window.translations && window.translations.edeskWidget) || {};

  // Storage keys with clear versioning
  const STORAGE = {
    WIDGET_ACTIVE: 'edesk_widget_state_v3',
    LAST_TOGGLE: 'edesk_widget_last_toggle'
  };

  // Debug mode - set to false for production
  const DEBUG = false;

  // Initialization tracking - reset on each page load
  let isInitialized = false;

  // Debounce protection - prevent multiple rapid toggles
  const DEBOUNCE_TIMEOUT = 2000; // 2 seconds between toggles

  // Flag to track if we're currently showing a confirmation dialog
  let confirmationInProgress = false;

  // Helper for controlled logging
  function log(message, data) {
    if (!DEBUG) return;
    const timestamp = new Date().toISOString().substr(11, 8);
    if (data) {
      console.log(`[${timestamp}] [WIDGET] ${message}`, data);
    } else {
      console.log(`[${timestamp}] [WIDGET] ${message}`);
    }
  }

  // Cookie utilities for persistent state management
  const Cookies = {
    set: function (name, value, minutes = 30) {
      let expires = '';
      if (minutes) {
        const date = new Date();
        date.setTime(date.getTime() + (minutes * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
      log('COOKIE SET', { name, value, expiresIn: minutes + ' minutes' });
    },

    get: function (name) {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
          const value = c.substring(nameEQ.length, c.length);
          log('COOKIE GET', { name, value });
          return value;
        }
      }
      log('COOKIE NOT FOUND', { name });
      return null;
    },

    remove: function (name) {
      document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      log('COOKIE REMOVED', { name });
    }
  };

  // Custom modal dialog that matches Shopify styling
  const CustomModal = {
    // Create and show a modal with the given title and message
    confirm: function (title, message, onConfirm, onCancel) {
      log('SHOWING CUSTOM MODAL', { title, message });

      // Create modal elements
      const modalOverlay = document.createElement('div');
      modalOverlay.className = 'edesk-modal-overlay';
      modalOverlay.style.position = 'fixed';
      modalOverlay.style.top = '0';
      modalOverlay.style.left = '0';
      modalOverlay.style.right = '0';
      modalOverlay.style.bottom = '0';
      modalOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      modalOverlay.style.zIndex = '9999';
      modalOverlay.style.display = 'flex';
      modalOverlay.style.alignItems = 'center';
      modalOverlay.style.justifyContent = 'center';
      modalOverlay.style.padding = '20px'; // Add padding to ensure modal doesn't touch screen edges
      modalOverlay.style.transition = 'opacity 0.2s ease';
      modalOverlay.style.opacity = '0';

      const modalContent = document.createElement('div');
      modalContent.className = 'edesk-modal-content';
      modalContent.style.backgroundColor = '#ffffff';
      modalContent.style.padding = '20px';
      modalContent.style.borderRadius = '8px';
      modalContent.style.width = '90%'; // Use percentage width for better responsiveness
      modalContent.style.maxWidth = '450px'; // Max width for larger screens
      modalContent.style.boxSizing = 'border-box'; // Include padding in width calculation
      modalContent.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';

      const modalTitle = document.createElement('h3');
      modalTitle.className = 'edesk-modal-title';
      modalTitle.textContent = title;
      modalTitle.style.marginTop = '0';
      modalTitle.style.marginBottom = '15px';
      modalTitle.style.fontSize = '18px';
      modalTitle.style.fontWeight = '600';
      modalTitle.style.lineHeight = '1.3';

      const modalMessage = document.createElement('p');
      modalMessage.className = 'edesk-modal-message';
      modalMessage.textContent = message;
      modalMessage.style.marginBottom = '20px';
      modalMessage.style.fontSize = '14px';
      modalMessage.style.lineHeight = '1.5';
      modalMessage.style.color = '#333';

      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'edesk-modal-buttons';
      buttonContainer.style.display = 'flex';
      buttonContainer.style.justifyContent = 'flex-end';
      buttonContainer.style.gap = '10px';

      // Media query for mobile devices
      const isMobile = window.innerWidth < 576;
      if (isMobile) {
        buttonContainer.style.flexDirection = 'column-reverse'; // Stack buttons on mobile
        buttonContainer.style.gap = '8px'; // Less gap on mobile
      }

      const cancelButton = document.createElement('button');
      cancelButton.className = 'edesk-modal-button edesk-modal-button-cancel';
      cancelButton.textContent = translations.cancel || 'Cancel';
      cancelButton.style.padding = isMobile ? '12px 16px' : '8px 16px'; // Larger touch target on mobile
      cancelButton.style.border = '1px solid #ddd';
      cancelButton.style.borderRadius = '4px';
      cancelButton.style.backgroundColor = '#f5f5f5';
      cancelButton.style.cursor = 'pointer';
      cancelButton.style.fontSize = '14px';
      cancelButton.style.width = isMobile ? '100%' : 'auto'; // Full width on mobile

      const confirmButton = document.createElement('button');
      confirmButton.className = 'edesk-modal-button edesk-modal-button-confirm';
      confirmButton.textContent = translations.ok || 'OK';
      confirmButton.style.padding = isMobile ? '12px 16px' : '8px 16px'; // Larger touch target on mobile
      confirmButton.style.border = 'none';
      confirmButton.style.borderRadius = '4px';
      confirmButton.style.backgroundColor = '#000000';
      confirmButton.style.color = '#ffffff';
      confirmButton.style.cursor = 'pointer';
      confirmButton.style.fontSize = '14px';
      confirmButton.style.width = isMobile ? '100%' : 'auto'; // Full width on mobile

      // Add elements to DOM
      buttonContainer.appendChild(cancelButton);
      buttonContainer.appendChild(confirmButton);

      modalContent.appendChild(modalTitle);
      modalContent.appendChild(modalMessage);
      modalContent.appendChild(buttonContainer);

      modalOverlay.appendChild(modalContent);
      document.body.appendChild(modalOverlay);

      // Define closeModal function inside the confirm method to ensure proper scope
      function closeModal() {
        modalOverlay.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(modalOverlay)) {
            document.body.removeChild(modalOverlay);
          }
        }, 200);
      }

      // Add event listeners
      cancelButton.addEventListener('click', function () {
        closeModal();
        if (typeof onCancel === 'function') {
          onCancel();
        }
      });

      confirmButton.addEventListener('click', function () {
        closeModal();
        if (typeof onConfirm === 'function') {
          onConfirm();
        }
      });

      // Add keyboard event listeners for accessibility
      modalOverlay.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          cancelButton.click();
        }
        if (e.key === 'Enter' && document.activeElement !== cancelButton) {
          confirmButton.click();
        }
      });

      // Add focus trap for accessibility
      const focusableElements = modalContent.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      modalOverlay.addEventListener('keydown', function (e) {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      });

      // Focus the confirm button by default
      confirmButton.focus();

      // Fade in the modal
      setTimeout(() => {
        modalOverlay.style.opacity = '1';
      }, 10);

      // Return the modal element (useful for testing)
      return modalOverlay;
    }
  };

  // State management
  const WidgetManager = {
    // Get current widget state from most reliable source
    getState: function () {
      // Check URL for force disable first
      const params = new URLSearchParams(window.location.search);
      if (params.get('widget') === 'off') {
        log('WIDGET STATE FROM URL: false (force disabled)');
        return false;
      }

      // Check cookie (most reliable)
      const cookieState = Cookies.get(STORAGE.WIDGET_ACTIVE);
      if (cookieState !== null) {
        const isActive = cookieState === 'true';
        log('WIDGET STATE FROM COOKIE:', isActive);
        return isActive;
      }

      // Default to inactive
      log('WIDGET STATE DEFAULT: false');
      return false;
    },

    // Set widget state in all available storage
    setState: function (isActive) {
      log('SETTING WIDGET STATE', isActive);

      // Always set cookie (most reliable)
      Cookies.set(STORAGE.WIDGET_ACTIVE, isActive ? 'true' : 'false');

      // Try localStorage as backup
      try {
        localStorage.setItem(STORAGE.WIDGET_ACTIVE, isActive ? 'true' : 'false');
      } catch (e) {
        // Ignore errors
      }

      // Verify state was set correctly
      const stateCheck = Cookies.get(STORAGE.WIDGET_ACTIVE);
      log('VERIFIED STATE SET', {
        expected: isActive ? 'true' : 'false',
        actual: stateCheck,
        correct: stateCheck === (isActive ? 'true' : 'false')
      });
    },

    // Force disable widget using all methods
    forceDisable: function () {
      log('FORCE DISABLING WIDGET');

      // Set cookie to disabled
      Cookies.set(STORAGE.WIDGET_ACTIVE, 'false', 30); // Explicitly set 30 minutes expiration

      // Try other storage mechanisms as backup
      try {
        localStorage.setItem(STORAGE.WIDGET_ACTIVE, 'false');
        sessionStorage.setItem(STORAGE.WIDGET_ACTIVE, 'false');
      } catch (e) {
        // Ignore errors
      }

      // Return true to indicate success
      return true;
    }
  };

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function () {
    initializeWidget();
  });

  /**
   * Initialize the widget based on current state
   * This allows initializing once per page load, but prevents multiple initializations
   * within the same page load session
   */
  function initializeWidget() {
    // Only allow one initialization per page load
    if (isInitialized) {
      log('WIDGET ALREADY INITIALIZED THIS SESSION, SKIPPING');
      return;
    }

    log('INITIALIZING WIDGET');
    log('URL:', window.location.href);

    // Reset confirmation flag on each page load
    confirmationInProgress = false;

    // Mark as initialized for this page session
    isInitialized = true;

    // Force disable widget if URL parameter is present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('widget') === 'off') {
      log('FOUND widget=off IN URL, FORCE DISABLING');
      WidgetManager.forceDisable();
      cleanUrl();
    }

    // Get current widget state
    const isActive = WidgetManager.getState();
    log('WIDGET ACTIVE STATE:', isActive);

    // Initialize based on state
    if (isActive) {
      log('WIDGET IS ACTIVE - LOADING WIDGET');
      loadEDeskWidget();
      updateButtonsToActiveState();
    } else {
      log('WIDGET IS INACTIVE - UPDATING UI ONLY');
      updateButtonsToInactiveState();
    }

    log('WIDGET INITIALIZATION COMPLETE');
  }

  /**
   * This is the main toggle function - used internally
   * We'll expose this to the global scope so it can be called from outside
   */
  function toggleWidgetInternal() {
    // IMPORTANT: Prevent activation while a confirmation is in progress
    if (confirmationInProgress) {
      log('TOGGLE BLOCKED - CONFIRMATION DIALOG IN PROGRESS');
      return;
    }

    // IMPORTANT: Debounce protection to prevent multiple rapid clicks
    const now = Date.now();
    let lastToggle = 0;

    try {
      lastToggle = parseInt(sessionStorage.getItem(STORAGE.LAST_TOGGLE) || '0');
    } catch (e) {
      // Ignore errors
    }

    // Check if we've toggled too recently
    if (now - lastToggle < DEBOUNCE_TIMEOUT) {
      log('TOGGLE DEBOUNCED - TOO SOON AFTER PREVIOUS TOGGLE', {
        timeSinceLast: now - lastToggle,
        minDelay: DEBOUNCE_TIMEOUT
      });
      return;
    }

    // Update last toggle time
    try {
      sessionStorage.setItem(STORAGE.LAST_TOGGLE, now.toString());
    } catch (e) {
      // Ignore storage errors
    }

    // Proceed with normal toggle
    log('TOGGLE WIDGET CALLED');

    try {
      // Get current state
      const isActive = WidgetManager.getState();
      log('CURRENT WIDGET STATE:', isActive);

      if (isActive) {
        // Currently active - show deactivation confirmation with custom modal
        log('WIDGET IS ACTIVE - SHOWING DEACTIVATION CONFIRMATION');

        // Set flag to prevent further toggles during confirmation
        confirmationInProgress = true;

        // Show custom modal dialog instead of browser confirm
        CustomModal.confirm(
          translations.disableTitle || 'Disable Live Chat Widget', // Custom title
          translations.disableMessage || 'Widget will be disabled for future page views. Refresh now to hide the widget?', // Message
          function () { // Confirm callback
            log('USER CONFIRMED DEACTIVATION - DISABLING WIDGET');

            // Force disable the widget
            WidgetManager.forceDisable();

            // CRITICAL: Delay the redirect to ensure state is saved
            setTimeout(function () {
              // Verify state before redirecting
              const verifyState = Cookies.get(STORAGE.WIDGET_ACTIVE);
              log('STATE BEFORE REDIRECT:', verifyState);

              // Redirect with parameters to ensure refresh happens
              const timestamp = Date.now();
              const redirectUrl = window.location.pathname + `?widget=off&t=${timestamp}`;
              log('REDIRECTING TO:', redirectUrl);
              window.location.href = redirectUrl;
            }, 200); // Slightly longer delay to ensure cookies are saved
          },
          function () { // Cancel callback
            log('USER CANCELLED DEACTIVATION - WIDGET REMAINS ACTIVE');

            // Reset confirmation flag after a short delay to allow current events to complete
            setTimeout(function () {
              confirmationInProgress = false;
              log('CONFIRMATION DIALOG COMPLETE - READY FOR NEW TOGGLES');
            }, 500);
          }
        );
      } else {
        // Currently inactive - activate immediately
        log('WIDGET IS INACTIVE - ACTIVATING');

        // Set state to active
        WidgetManager.setState(true);

        // Load the widget
        loadEDeskWidget();

        // Update UI
        updateButtonsToActiveState();

        log('WIDGET ACTIVATED SUCCESSFULLY');
      }
    } catch (err) {
      // Reset confirmation flag in case of errors
      confirmationInProgress = false;
      console.error('Error in toggleWidget:', err);
    }
  }

  /**
   * Update all button labels to active state
   */
  function updateButtonsToActiveState() {
    log('UPDATING BUTTONS TO ACTIVE STATE');

    try {
      const buttons = document.querySelectorAll('.contact-item[data-trigger-chat="true"]');
      log('FOUND BUTTONS:', buttons.length);

      if (!buttons || buttons.length === 0) return;

      buttons.forEach(function (button, index) {
        if (!button) return;

        const labelElement = button.querySelector('.body-m');
        if (!labelElement) return;

        // Save the secondary text span if it exists
        const secondarySpan = labelElement.querySelector('.body-s');
        const secondaryText = secondarySpan ? secondarySpan.innerHTML : '';

        // Get active suffix for translation
        const activeSuffix = translations.activeSuffix || ' - Active';

        // Get original text (just the first line)
        const originalLabelParts = labelElement.innerHTML.split('<br>');
        const originalLabel = button.getAttribute('data-original-label') ||
          (originalLabelParts[0] || '').replace(activeSuffix, '');

        // Store original label
        if (!button.hasAttribute('data-original-label')) {
          button.setAttribute('data-original-label', originalLabel);
          log(`STORED ORIGINAL LABEL FOR BUTTON ${index}:`, originalLabel);
        }

        // Set active text while preserving secondary text
        const newText = originalLabel + activeSuffix;
        if (secondarySpan) {
          labelElement.innerHTML = newText + '<br><span class="body-s small-hide">' + secondaryText + '</span>';
        } else {
          labelElement.textContent = newText;
        }
        log(`UPDATED BUTTON ${index} TO ACTIVE:`, newText);
      });
    } catch (err) {
      console.error('Error updating buttons to active state:', err);
    }
  }

  /**
   * Update all button labels to inactive state
   */
  function updateButtonsToInactiveState() {
    log('UPDATING BUTTONS TO INACTIVE STATE');

    try {
      const buttons = document.querySelectorAll('.contact-item[data-trigger-chat="true"]');
      log('FOUND BUTTONS:', buttons.length);

      if (!buttons || buttons.length === 0) return;

      buttons.forEach(function (button, index) {
        if (!button) return;

        const labelElement = button.querySelector('.body-m');
        if (!labelElement) return;

        // Save the secondary text span if it exists
        const secondarySpan = labelElement.querySelector('.body-s');
        const secondaryText = secondarySpan ? secondarySpan.innerHTML : '';

        // Get active suffix for translation (used when removing it)
        const activeSuffix = translations.activeSuffix || ' - Active';

        // Get original text (just the first line)
        const originalLabelParts = labelElement.innerHTML.split('<br>');
        const originalLabel = button.getAttribute('data-original-label') ||
          (originalLabelParts[0] || '').replace(activeSuffix, '');

        // Store original label
        if (!button.hasAttribute('data-original-label')) {
          button.setAttribute('data-original-label', originalLabel);
          log(`STORED ORIGINAL LABEL FOR BUTTON ${index}:`, originalLabel);
        }

        // Set inactive text while preserving secondary text
        if (secondarySpan) {
          labelElement.innerHTML = originalLabel + '<br><span class="body-s small-hide">' + secondaryText + '</span>';
        } else {
          labelElement.textContent = originalLabel;
        }
        log(`UPDATED BUTTON ${index} TO INACTIVE:`, originalLabel);
      });
    } catch (err) {
      console.error('Error updating buttons to inactive state:', err);
    }
  }

  /**
   * Clean URL without page refresh
   */
  function cleanUrl() {
    log('CLEANING URL');

    try {
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
        log('URL CLEANED SUCCESSFULLY');
      } else {
        log('HISTORY API NOT AVAILABLE - CANNOT CLEAN URL');
      }
    } catch (err) {
      console.error('Error cleaning URL:', err);
    }
  }

  /**
   * Load the eDesk widget
   */
  function loadEDeskWidget() {
    log('LOADING EDESK WIDGET');

    try {
      // Only initialize once
      if (window._xsq) {
        log('WIDGET ALREADY LOADED, SKIPPING');
        return;
      }

      log('CREATING WIDGET IFRAME');
      (function (e, f) {
        window._xsq = window._xsq || [];
        var a = document.createElement("iframe");
        a.src = "javascript:false";
        a.title = "";
        a.role = "presentation";
        (a.frameElement || a).style.cssText = "display: none";
        document.body.appendChild(a);
        try {
          var b = a.contentWindow.document;
        } catch (c) {
          var d = document.domain;
          a.src = "javascript:var d=document.open();d.domain='" + d + "';void(0);";
          b = a.contentWindow.document;
        }
        b.open()._l = function () {
          var c = this.createElement("script");
          if (d) this.domain = d;
          c.id = "js-iframe-async";
          c.src = "https://" + e + f;
          this.body.appendChild(c);
        };
        b.write('<body onload="document._l();">');
        b.close();
      })("widgets.xsellco.com", "/js/widgets.js");

      window._xsq.push(["load", "7x15ubd2a"]);
      log('WIDGET LOAD COMMAND SENT');
    } catch (err) {
      console.error('Error loading eDesk widget:', err);
    }
  }

  // Expose ONLY the toggleWidget function to the global scope
  window.toggleWidget = toggleWidgetInternal;
})();
